import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'

const getMeetTypeLabel = (type) => {
    return Number(type) === 0 ? '공개' : '비공개'
}

const PANEL_SX = {
    height: '100%',
    p: 2,
    borderRadius: '4px',
    borderColor: 'rgba(148, 163, 184, 0.24)',
    backgroundColor: '#fff',
}

function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...PANEL_SX, ...sx }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function MeetImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="대표 이미지"
            description="앱에 노출되는 우리끼리 대회 대표 이미지를 확인합니다."
        >
            <Box
                sx={{
                    minHeight: 320,
                    maxHeight: 520,
                    borderRadius: '4px',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc',
                    overflow: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    component="img"
                    src={imageSrc || '/images/noimage.jpg'}
                    alt="우리끼리 대회 대표 이미지"
                    sx={{
                        width: '100%',
                        maxHeight: 520,
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
            </Box>
        </FormSection>
    )
}

export default function AppMeetWe(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.meetId
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchCategory: '', searchType: '' }
    const curPage = locationState.curPage || 1
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({})
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [image, setImage] = useState('')

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 우리끼리 대회 상세를 불러올 수 없습니다.'
            setParams({})
            setImage('')
            setHasDetail(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appMeetWe/getDetail', { meetId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.meetId) {
                    setParams({})
                    setImage('')
                    setDetailErrorMessage('우리끼리 대회 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                setImage(baseParams.image ? `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}` : '/images/noimage.jpg')
                setParams(baseParams)
                setHasDetail(true)
                setDetailErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '우리끼리 대회 상세를 불러오지 못했습니다.'
                setParams({})
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '우리끼리 대회 상세를 불러오지 못했습니다.'
            setParams({})
            setImage('')
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, id])

    useEffect(() => {
        getData()
    }, [getData])

    const moveToList = (snackbarState) => {
        history.push(createListLinkTarget({
            pathname: '/appMeetWes',
            search: listSearch,
            curPage,
            searchText,
            searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {}),
        }))
    }

    const handleTitleChange = (event) => {
        setParams((prevParams) => ({
            ...prevParams,
            title: event.target.value,
        }))
    }

    const updateTitle = async (title) => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appMeetWe/update', {
                meetId: id,
                title,
            })

            if (data.code === 200) {
                moveToList({
                    severity: 'success',
                    message: data.message || '제목을 수정했습니다.',
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '우리끼리 대회 제목을 저장하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('우리끼리 대회 제목을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (params.title || '').trim()

        setParams((prevParams) => ({
            ...prevParams,
            title: nextTitle,
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제목을 입력해주세요.')
            return
        }

        if (nextTitle.length > 60) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제목은 60자 이내로 입력해주세요.')
            return
        }

        updateTitle(nextTitle)
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="우리끼리 대회 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appMeetWes" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>우리끼리 대회 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>우리끼리 대회 상세</Title>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={4}>
                            <MeetImagePreview imageSrc={image}/>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="대회 정보"
                                description="우리끼리 대회의 기본 정보와 공개 상태를 확인합니다."
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                label="제목"
                                                id="title"
                                                name="title"
                                                value={params.title || ''}
                                                onChange={handleTitleChange}
                                                disabled={saving}
                                                size="small"
                                                inputProps={{ maxLength: 60 }}
                                                helperText={`${(params.title || '').length}/60자`}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="일정"
                                                id="cStartTime"
                                                name="cStartTime"
                                                value={params.cStartTime && params.cEndTime ? `${params.cStartTime} ~ ${params.cEndTime}` : ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="거리"
                                                id="course"
                                                name="course"
                                                value={params.course ? `${params.course}km` : ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="인원"
                                                id="maxNum"
                                                name="maxNum"
                                                value={params.maxNum > 0 ? `${params.cnt}명 / ${params.maxNum}` : `${params.cnt}명 / 제한없음`}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="공개여부"
                                                id="type"
                                                name="type"
                                                value={getMeetTypeLabel(params.type)}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="대회 내용"
                                description="앱 상세 화면에 표시되는 내용을 확인합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        multiline
                                        rows={6}
                                        id="content"
                                        name="content"
                                        value={params.content || ''}
                                        size="small"
                                        InputProps={{ readOnly: true }}
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        variant="formAction"
                        path="/appMeetWes"
                        search={listSearch}
                        visible
                        searchText={searchText}
                        searchSelect={searchSelect}
                        curPage={curPage}
                        submitDisabled={saving || !(params.title || '').trim()}
                        submitLabel={saving ? '저장 중' : '저장'}
                        backDisabled={saving}
                        actionDescription="제목만 변경되며 다른 대회 정보는 그대로 유지됩니다."
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
